import { useEffect, useState } from 'react';
import { useI18n } from '@/shared/i18n';

export function PwaStatus() {
  const { t } = useI18n();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    if (import.meta.env.PROD && 'serviceWorker' in navigator) {
      let refreshing = false;
      const onControllerChange = () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

      // `updateViaCache: 'none'` keeps the update check off the HTTP cache. The
      // build stamps a new version into sw.js precisely so a deploy can replace
      // a bad worker; a cached copy of the old file would hide that it changed.
      void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).then((registration) => {
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setWaitingWorker(newWorker);
              setUpdateDismissed(false);
            }
          });
        });

        const checkUpdate = () => {
          void registration.update().catch(() => undefined);
        };

        const onVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            checkUpdate();
          }
        };

        window.addEventListener('focus', checkUpdate);
        document.addEventListener('visibilitychange', onVisibilityChange);
      }).catch(() => undefined);
    } else {
      // A development worker would serve stale Vite modules after HMR and make
      // browser verification misleading. Production still gets the full PWA.
      void navigator.serviceWorker?.getRegistrations().then((registrations) =>
        Promise.all(registrations.map((registration) => registration.unregister())),
      ).catch(() => undefined);
    }

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.offline = online ? 'false' : 'true';
    return () => { delete document.documentElement.dataset.offline; };
  }, [online]);

  const handleUpdate = async () => {
    // The worker captured when the banner appeared can go redundant when a later
    // deploy installs a newer one; posting to it does nothing. Ask the
    // registration for the worker waiting now, and reload if nothing takes over.
    const registration = await navigator.serviceWorker?.getRegistration('/').catch(() => undefined);
    const worker = registration?.waiting ?? (waitingWorker?.state === 'installed' ? waitingWorker : null);
    if (!worker) {
      window.location.reload();
      return;
    }
    worker.postMessage({ type: 'SKIP_WAITING' });
    window.setTimeout(() => window.location.reload(), 3000);
  };

  return (
    <>
      {!online && (
        <div role="status" aria-live="assertive" className="fixed inset-x-0 bottom-14 z-[60] border-t border-warning bg-warning-soft px-4 py-3 text-center text-sm font-medium text-fg sm:bottom-0 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          {t('common.offline')}
        </div>
      )}
      {online && waitingWorker && !updateDismissed && (
        <div role="status" aria-live="polite" className="fixed inset-x-0 bottom-14 z-[58] flex items-center justify-center gap-3 border-t border-accent/30 bg-surface px-4 py-3 text-sm text-fg shadow-lg sm:bottom-0 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          <span className="min-w-0 flex-1 sm:flex-none">{t('common.updateAvailable')}</span>
          <button type="button" className="tap-target inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-accent px-3.5 py-1 text-xs font-semibold leading-none text-accent-fg transition-opacity hover:opacity-90" onClick={() => { void handleUpdate(); }}>
            {t('common.updateApp')}
          </button>
          <button type="button" className="tap-target inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-2 text-xs leading-none text-muted hover:text-fg" onClick={() => setUpdateDismissed(true)}>
            {t('common.dismiss')}
          </button>
        </div>
      )}
    </>
  );
}

